import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  findingHeadline,
  findingSeverityVariant,
  summarizeSecurityScan,
  tableNameFromObject,
  type SecurityAdvisorReport,
  type SecurityFinding,
} from "@/lib/advisorsSecurity";
import { ROUTES } from "@/routes";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";
import { Badge } from "@/components/ui/badge";

export function AdvisorsSecurityPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [report, setReport] = useState<SecurityAdvisorReport | null>(null);

  async function load(rerun: boolean) {
    if (!id) return;
    const { data } = rerun
      ? await api.runSecurityAdvisor(id)
      : await api.getSecurityAdvisor(id);
    setReport(data);
  }

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        await load(false);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const findings = report?.findings ?? [];
  const summary = useMemo(() => summarizeSecurityScan(findings), [findings]);

  if (loading) return <Spinner label="Scanning security…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Advisors"
        title="Security"
        description="Live scan of row-level security on exposed project tables. Findings update when you enable RLS or add a policy."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Findings" value={summary.total} />
        <StatTile label="Errors" value={summary.errors} />
        <StatTile label="Warnings" value={summary.warnings} />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-text-muted">
          {report?.scanned_at
            ? `Last scan ${new Date(report.scanned_at).toLocaleString()}`
            : "Not scanned yet"}
        </p>
        <button
          type="button"
          className="btn-primary"
          disabled={running}
          onClick={() => {
            setRunning(true);
            setError(null);
            void load(true)
              .catch((e) => setError(e))
              .finally(() => setRunning(false));
          }}
        >
          {running ? "Scanning…" : "Rerun scan"}
        </button>
      </div>

      {findings.length === 0 ? (
        <div className="card p-5 text-sm text-text-secondary">
          No RLS findings on exposed tables.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Object</th>
                <th>Issue</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {findings.map((finding) => (
                <FindingRow key={findingKey(finding)} projectId={id} finding={finding} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function findingKey(finding: SecurityFinding): string {
  return `${finding.class}:${finding.object}`;
}

function FindingRow({
  projectId,
  finding,
}: {
  projectId: string;
  finding: SecurityFinding;
}) {
  const table = tableNameFromObject(finding.object);
  return (
    <tr>
      <td>
        <Badge variant={findingSeverityVariant(finding.severity)}>
          {finding.severity}
        </Badge>
      </td>
      <td className="font-mono text-xs">{finding.object}</td>
      <td>
        <div className="text-sm text-text-primary">{findingHeadline(finding)}</div>
        <div className="mt-1 text-xs text-text-muted">{finding.detail}</div>
        <div className="mt-1 font-mono text-[10px] text-text-muted">
          {finding.class}
        </div>
      </td>
      <td>
        <Link
          className="text-xs text-secondary hover:underline"
          to={`${ROUTES.permissions(projectId)}?table=${encodeURIComponent(table)}`}
        >
          Open policies
        </Link>
      </td>
    </tr>
  );
}
